import { Linkedin, Instagram, Twitter } from "lucide-react";
import Logo from "./Logo";
import { COPY } from "@/content/copy";
import cashewLogo from "@/assets/logos/cashew-logo.png";
import tabbyLogo from "@/assets/logos/tabby-logo.png";
import tamaraLogo from "@/assets/logos/tamara-logo.png";
import VerificationModal from "./VerificationModal";

const Footer = () => {
  return (
    <footer 
      className="bg-[hsl(var(--flexaura-primary-dark))] text-white py-16 px-4"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div className="md:col-span-1">
            <Logo className="text-white mb-4" />
            <p className="text-sm text-white/80 font-body mb-4">
              {COPY.footerTagline}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://linkedin.com/company/flexaura" 
                className="p-2 hover:bg-white/10 rounded-lg smooth-transition"
                aria-label="Follow Flexaura on LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com/flexaura" 
                className="p-2 hover:bg-white/10 rounded-lg smooth-transition"
                aria-label="Follow Flexaura on Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://twitter.com/flexaura" 
                className="p-2 hover:bg-white/10 rounded-lg smooth-transition"
                aria-label="Follow Flexaura on Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Services</h3>
            <nav className="flex flex-col gap-3">
              <a href="/travel" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Flex Travel</a>
              <a href="/education" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Flex Education</a>
              <a href="/healthcare" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Flex Healthcare</a>
              <a href="/marketplace" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Marketplace</a>
            </nav>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Company</h3>
            <nav className="flex flex-col gap-3">
              <a href="/about" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">About Us</a>
              <a href="/partners" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Partners</a>
              <a href="/investors" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Investors</a>
              <a href="/faq" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">FAQ</a>
              <a href="/contact" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Contact</a>
            </nav>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Legal</h3>
            <nav className="flex flex-col gap-3">
              <a href="/privacy" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Privacy Policy</a>
              <a href="/terms" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Terms of Service</a>
              <a href="/refund" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Refund Policy</a>
              <a href="/transparency" className="text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">Transparency</a>
              <VerificationModal
                trigger={
                  <button className="text-left text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition">
                    How we verify listings
                  </button>
                }
              />
            </nav>
          </div>
        </div>

        {/* Licensed Partners */}
        <div className="border-t border-white/20 pt-8 mb-8">
          <p className="text-xs text-white/50 uppercase tracking-wider mb-4 text-center font-body">Our Licensed Partners</p>
          <div className="flex items-center justify-center gap-8">
            <img src={tabbyLogo} alt="Tabby" className="h-6 opacity-70 brightness-0 invert" />
            <img src={tamaraLogo} alt="Tamara" className="h-6 opacity-70 brightness-0 invert" />
            <img src={cashewLogo} alt="Cashew" className="h-6 opacity-70 brightness-0 invert" />
          </div>
        </div>
        
        {/* Support */}
        <div className="border-t border-white/20 pt-6 mb-6 text-center">
          <a
            href="https://wa.me/971000000000"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[hsl(var(--flexaura-accent))] smooth-transition font-body"
          >
            Support: Chat on WhatsApp →
          </a>
        </div>

        {/* Regulatory disclaimer */}
        <div className="border-t border-white/20 pt-6 text-center">
          <p className="text-xs text-white/50 font-body mb-4 max-w-3xl mx-auto leading-relaxed">
            Flexaura is a financial comparison and referral marketplace. We are not a licensed lender, payment processor, or financial advisor. BNPL products are subject to UAE Central Bank regulations. Maximum BNPL credit limit AED 20,000 per borrower. Always read the terms of your chosen provider before proceeding.
          </p>
          <p className="text-sm text-white/70 font-body">
            © {new Date().getFullYear()} Flexaura. All rights reserved. | Monthly payment solutions for travel, education, and healthcare.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
